import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PhoneCall, Calendar, Flame, TrendingUp, ChevronRight, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { useBranding, withAlpha } from "../../lib/branding";
import ReferralWidget from "../../components/ReferralWidget";

const DISP_TINT = {
  Booked: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  "Hot Lead": "bg-amber-500/10 text-amber-300 border-amber-500/30",
  "Got Email": "bg-sky-500/10 text-sky-300 border-sky-500/30",
  "Not Interested": "bg-rose-500/10 text-rose-300 border-rose-500/30",
  "Asked for DNC": "bg-rose-500/10 text-rose-300 border-rose-500/30",
};

export default function PortalDashboard() {
  const { slug } = useParams();
  const branding = useBranding();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/portal/performance").then(({ data }) => {
      setStats(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const accent = branding?.accent_color || "#FFB000";
  const clientName = branding?.name || "your business";

  if (loading) {
    return <div className="text-white/50 py-12 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }
  if (!stats) {
    return <div className="text-white/50 py-12">Couldn't load your dashboard.</div>;
  }

  const kpis = [
    { label: "Calls today", value: stats.calls_today, icon: PhoneCall },
    { label: "Calls this week", value: stats.calls_week, icon: PhoneCall },
    { label: "Booked appts", value: stats.appointments_total, icon: Calendar },
    { label: "Hot leads", value: stats.hot_leads, icon: Flame },
  ];

  return (
    <div className="space-y-8" data-testid="portal-dashboard">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-medium mb-2" style={{ color: accent }}>
          Welcome back
        </div>
        <h1 className="text-3xl font-bold tracking-tight">{clientName} dashboard</h1>
        <p className="text-sm text-white/50 mt-2">Every call your AI receptionist takes — at a glance.</p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="portal-kpis">
        {kpis.map(({ label, value, icon: Icon }) => (
          <div key={label} className="bg-[#121214] border border-white/5 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/40">{label}</div>
              <Icon className="w-4 h-4" style={{ color: accent }} strokeWidth={1.75} />
            </div>
            <div className="text-3xl font-bold" style={{ color: accent }}>{value}</div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <div className="bg-[#121214] border border-white/5 rounded-xl p-5">
          <div className="text-[10px] uppercase tracking-[0.12em] text-white/40 mb-2">Booking rate</div>
          <div className="text-3xl font-bold">{stats.booking_rate_per_100}%</div>
          <div className="text-xs text-white/40 mt-1.5">per 100 calls answered</div>
        </div>
        <div className="bg-[#121214] border border-white/5 rounded-xl p-5">
          <div className="text-[10px] uppercase tracking-[0.12em] text-white/40 mb-2">Answer rate</div>
          <div className="text-3xl font-bold">{stats.answer_rate_per_100}%</div>
          <div className="text-xs text-white/40 mt-1.5">connections that picked up</div>
        </div>
        <div className="bg-[#121214] border border-white/5 rounded-xl p-5">
          <div className="text-[10px] uppercase tracking-[0.12em] text-white/40 mb-2">Upcoming demos</div>
          <div className="text-3xl font-bold">{stats.appointments_upcoming}</div>
          <div className="text-xs text-white/40 mt-1.5">confirmed in your calendar</div>
        </div>
      </div>

      {/* Recent calls */}
      <div className="bg-[#121214] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Recent calls</h2>
          <Link
            to={`/portal/${slug}/calls`}
            className="text-sm font-medium flex items-center gap-1 hover:underline"
            style={{ color: accent }}
            data-testid="portal-view-all-calls"
          >
            View all <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        {stats.recent_calls?.length ? (
          <div className="space-y-2">
            {stats.recent_calls.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="text-white/85 truncate">{c.contact_name || c.phone || "Unknown"}</div>
                  <div className="text-[11px] text-white/40 mt-0.5">{c.created_at ? new Date(c.created_at).toLocaleString() : ""}</div>
                </div>
                {c.disposition && (
                  <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded border font-medium ${DISP_TINT[c.disposition] || "bg-white/5 text-white/60 border-white/10"}`}>
                    {c.disposition}
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="py-12 text-center text-white/40 text-sm" data-testid="portal-no-calls">
            <PhoneCall className="w-8 h-8 mx-auto mb-3 text-white/20" strokeWidth={1.5} />
            No calls yet — when your AI starts taking calls they'll show up here.
          </div>
        )}
      </div>

      {/* Referral widget — earn 1 free month */}
      <ReferralWidget />
    </div>
  );
}
